import { useRef, useCallback, useMemo, useEffect } from 'react';
import { useParsed } from '@refinedev/core';
import type { FormInstance } from 'antd';
import { getFilterValue } from '@/utils/filters';

interface UseTableSearchOptions {
  /**
   * Whether this table's filters live in the URL. Embedded tables that opt out
   * of `syncWithLocation` must not hydrate from a term another table wrote.
   */
  syncWithLocation?: boolean;
}

export const useTableSearch = ({
  syncWithLocation = true,
}: UseTableSearchOptions = {}) => {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formRef = useRef<FormInstance | undefined>(undefined);
  // Refine's router binding already parses the query string into filters.
  const { params: urlParams } = useParsed();

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Search filter transformation
  const onSearch = useCallback((params: unknown) => {
    const { q } = params as { q?: string };

    return [
      {
        field: 'q',
        operator: 'eq' as const,
        value: q && q.trim() !== '' ? q : undefined,
      },
    ];
  }, []);

  // Create debounced submit function
  const createDebouncedSubmit = useCallback((form?: FormInstance) => {
    // Kept so the mount effect below can put a URL term back in the box.
    formRef.current = form;

    return (value: string) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        form?.setFieldsValue({ q: value });
        form?.submit();
      }, 300);
    };
  }, []);

  /**
   * The URL owns `q`: Refine applies it to the request but leaves the search
   * box empty, so a reloaded or shared link looks unfiltered. Put the term
   * back once, on mount — never submit, the rows are already filtered by it.
   */
  useEffect(() => {
    if (!syncWithLocation) return;
    const term = getFilterValue<string>(urlParams?.filters, 'q', 'eq');
    if (term && !formRef.current?.getFieldValue('q')) {
      formRef.current?.setFieldsValue({ q: term });
    }
    // Mount only: after this the form owns the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({ onSearch, createDebouncedSubmit }),
    [onSearch, createDebouncedSubmit]
  );
};
